import React, { useEffect, useRef, useState } from "react";
import {
  KoBadge,
  KoButton,
  KoDateFormatter,
  KoDP,
  KoInput,
  KoText,
  KoTooltips
} from "packages";
import { Excalidraw, exportToCanvas } from "@excalidraw/excalidraw";
import "@excalidraw/excalidraw/index.css";
import styles from "./whiteboard.module.scss";
import KoIcon from "@kwapio/icons";
import { useSelector } from "react-redux";
import { Header } from "antd/es/layout/layout";
import FileTypeIcon from "packages/richText/fileTypeIcon";
import { getCookie } from "shared/utils/cookies";
import { getSettings } from "shared/store/settingsSlice";
import { getUserById } from "shared/store/userByIdSlice";
import { useDispatch } from "react-redux";

const AUTOSAVE_DELAY = 2000;

const createEmptySlide = () => ({
  id: crypto.randomUUID(),
  elements: [],
  files: {},
  thumbnail: null,
  bgColor: "#ffffff"
});

const getElementsHash = (elements = []) =>
  JSON.stringify(
    elements.map(el => ({
      id: el.id,
      type: el.type,
      x: el.x,
      y: el.y,
      width: el.width,
      height: el.height,
      angle: el.angle,
      points: el.points,
      text: el.text,
      version: el.version
    }))
  );

export default function KoWhiteboard({ editorProps, key}) {
  const { initialValues, fileDetails,
    isLoading, savingTextStatus, onChangeEditorValues,
    onUpdateName,
    totalComments,
    onDownload, onShare, onComments, canEdit, canComment } = editorProps;


  const apiRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const lastSavedHashRef = useRef(null);
  const isDirtyRef = useRef(false);
  const isProgrammaticUpdateRef = useRef(false);

  const [slides, setSlides] = useState(initialValues || []);
  const [activeIndex, setActiveIndex] = useState(0);
  const [fileName, setRenameDocument] = useState(
    fileDetails?.name || "Untitled Document"
  );

  const accountById = useSelector(state => state?.accountById);
  const userById = useSelector(state => state?.userById);
  const accountDetails = accountById?.data;
  const userDetails = userById?.data;
  const dispatch = useDispatch();

  useEffect(() => {
    if (getCookie('userId')) {
      dispatch(getSettings(getCookie('accountId')));
      dispatch(getUserById(getCookie('userId')));
    }
  }, []);

  useEffect(() => {
    if (!Array.isArray(initialValues)) return;

    setSlides(initialValues);
    setActiveIndex(0);

    lastSavedHashRef.current = getElementsHash(
      initialValues[0]?.elements || []
    );

    isDirtyRef.current = false;
    isProgrammaticUpdateRef.current = true;

    requestAnimationFrame(() => {
      isProgrammaticUpdateRef.current = false;
    });
  }, [initialValues]);

  useEffect(() => {
    setRenameDocument(fileDetails?.name || 'Untitled Document');
  }, [fileDetails?.name]);


  useEffect(() => {
    if (!apiRef.current) return;
    const slide = slides[activeIndex];
    if (!slide) return;

    isProgrammaticUpdateRef.current = true;

    apiRef.current.updateScene({
      elements: slide.elements || [],
      files: slide.files || {},
      appState: {
        viewBackgroundColor: slide.bgColor || "#ffffff"
      }
    });

    lastSavedHashRef.current = getElementsHash(slide.elements || []);
    isDirtyRef.current = false;

    requestAnimationFrame(() => {
      isProgrammaticUpdateRef.current = false;
    });
  }, [activeIndex, slides]);

  const generateThumbnail = async(elements, files, bgColor) => {
    const canvas = await exportToCanvas({
      elements,
      files,
      appState: {
        viewBackgroundColor: bgColor,
        width: 220,
        height: 140
      }
    });
    return canvas.toDataURL("image/png");
  };

  const autosaveCurrentSlide = async() => {
    if (!apiRef.current || !slides[activeIndex]) return;

    const elements = apiRef.current.getSceneElements();
    const files = apiRef.current.getFiles();
    const newHash = getElementsHash(elements);

    if (newHash === lastSavedHashRef.current) return;

    const thumbnail = await generateThumbnail(
      elements,
      files,
      slides[activeIndex].bgColor
    );

    const clonedElements = structuredClone(elements);
    const clonedFiles = structuredClone(files);

    lastSavedHashRef.current = newHash;
    isDirtyRef.current = false;

    setSlides(prev => {
      const updated = [...prev];
      updated[activeIndex] = {
        ...updated[activeIndex],
        elements: clonedElements,
        files: clonedFiles,
        thumbnail
      };
      onChangeEditorValues?.(updated, 'whiteboard');
      return updated;
    });
  };

  const triggerAutosave = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = setTimeout(() => {
      if (isDirtyRef.current) {
        autosaveCurrentSlide();
      }
    }, AUTOSAVE_DELAY);
  };

  const handleExcalidrawChange = elements => {
    if (isProgrammaticUpdateRef.current) return;
    const newHash = getElementsHash(elements);
    if (newHash !== lastSavedHashRef.current) {
      isDirtyRef.current = true;
      triggerAutosave();
    }
  };

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  const switchSlide = async index => {
    if (index === activeIndex) return;
    await autosaveCurrentSlide();
    setActiveIndex(index);
  };

  const addSlide = async() => {
    await autosaveCurrentSlide();
    setSlides(prev => {
      const updated = [...prev, createEmptySlide()];
      setActiveIndex(updated.length - 1);
      onChangeEditorValues?.(updated, 'whiteboard');
      return updated;
    });
  };

  const duplicateSlide = async index => {
    await autosaveCurrentSlide();
    setSlides(prev => {
      const copy = { ...prev[index], id: crypto.randomUUID() };
      const updated = [
        ...prev.slice(0, index + 1),
        copy,
        ...prev.slice(index + 1)
      ];
      setActiveIndex(index + 1);
      onChangeEditorValues?.(updated, 'whiteboard');
      return updated;
    });
  };

  const deleteSlide = async index => {
    if (slides?.length === 1) return;
    await autosaveCurrentSlide();
    setSlides(prev => {
      const updated = prev.filter((_, i) => i !== index);
      setActiveIndex(Math.max(0, index - 1));
      onChangeEditorValues?.(updated, 'whiteboard');
      return updated;
    });
  };

  const moveSlide = (from, to) => {
    if (to < 0 || to >= slides?.length) return;
    setSlides(prev => {
      const updated = [...prev];
      const [item] = updated.splice(from, 1);
      updated.splice(to, 0, item);
      onChangeEditorValues?.(updated, 'whiteboard');
      return updated;
    });
    setActiveIndex(to);
  };


  return (
    <div className={styles.wrapper}>
      <Header className={styles.shallowHeader}></Header>
      <Header className={styles.globalHeader}>
        <div className={styles.docHeader}>
          <div className={styles.docHeaderLeft}>
            <FileTypeIcon type={'docx'} size={32} />
            <KoInput disabled={!canEdit} onChange={(e) => setRenameDocument(e.target.value)} onBlur={() => onUpdateName(fileName)} className={`${styles.titleInput} ${canEdit ? '' : styles.titleInputDisabled}`} value={fileName} />
          </div>
          <div className={styles.docHeaderRight}>
            {isLoading && <div className={styles.loader}>
              <KoIcon color="#007867" name="RefreshOutlined" width={16} height={16} />
              <KoText className={styles.loaderText} text={savingTextStatus || 'asdasdasd'} />
            </div>}
            <KoTooltips title={<div className={styles.lastUpdated}>
              <KoText className={styles.lastUpdatedText} text={'Last Updated:'} />
              <KoDateFormatter className={styles.lastUpdatedDate} data={fileDetails?.updatedAt !== '0001-01-01T00:00:00Z' ? fileDetails?.updatedAt : fileDetails?.createdAt} />
            </div>} placement="bottom">
              <KoButton iconBtn={true} color="default" variant="filled" icon={<KoIcon width={16} height={16} name={'LogOutlined'} />} />
            </KoTooltips>
            <KoTooltips title={'Comments'} placement="bottom">
              <KoBadge count={totalComments} >
                <KoButton disabled={!canEdit && !canComment} onClick={onComments} iconBtn={true} color="default" variant="filled" icon={<KoIcon width={20} height={20} name={'ChatOutlined'} />} />
              </KoBadge>
            </KoTooltips>
            <KoTooltips
              title={`Shared with ${fileDetails?.sharedWith?.users?.length || 0
                } ${fileDetails?.sharedWith?.users?.length === 1 ? 'person' : 'people'} & ${fileDetails?.sharedWith?.groups?.length || 0
                } ${fileDetails?.sharedWith?.groups?.length === 1 ? 'group' : 'groups'}`}
              placement="bottom"
            >
              <KoButton disabled={!canEdit} onClick={onShare} className={styles.shareButton} color="default" variant="filled" label='Share' icon={<KoIcon width={20} height={20} name={'AddtoGroupsOutlined'} color={'#FFF'} />} />
            </KoTooltips>
            <KoTooltips title={'Download'} placement="bottom">
              <KoButton loader={isLoading} onClick={onDownload} iconBtn={true} color="default" variant="filled" icon={<KoIcon width={20} height={20} name={'DownloadOutlined'} />} />
            </KoTooltips>
            <KoButton label="+ Add Slide" type="primary" onClick={addSlide} />
            <div className={styles.userWrapper} >
              {accountDetails?.companyName && <KoText ellipsis={true} className={styles.name} text={`${accountDetails?.companyName}.`} />}
              <div className={`${styles.headCircle}  ${styles[userDetails ?
                userDetails?.profile?.firstName?.charAt(0).toLowerCase() : 'a']}`}>
                {userDetails?.profile?.['logo'] ?
                  <KoDP shape={'circle'} logo={userDetails?.profile?.logo} width={userDetails?.profile?.logo?.[0]?.name ? 30 : 30} height={userDetails?.profile?.logo?.[0]?.name ? 30 : 30} />
                  :
                  <KoText className={`${styles.letter}`} text={userDetails?.profile?.firstName?.charAt(0)} />
                }
              </div>
            </div>
          </div>
        </div>
      </Header>

      <div className={styles.content}>
        <div className={styles.excalidraw}>
          <Excalidraw
            excalidrawAPI={api => (apiRef.current = api)}
            onChange={handleExcalidrawChange}
          />
        </div>

        <div className={styles.slides}>
          {slides?.map((slide, i) => (
            <div
              key={slide.id}
              className={`${styles.slide} ${i === activeIndex ? styles.active : ""
                }`}
            >
              <div
                onClick={() => switchSlide(i)}
                style={{
                  height: 120,
                  background: slide.thumbnail
                    ? `url(${slide.thumbnail}) center/contain no-repeat`
                    : "#eee"
                }}
              />
              <div className={styles.controls}>
                {i > 0 && (
                  <KoButton
                    className={styles.smallBtn}
                    icon={<KoIcon name="ArrowUpOutlined" width={12} height={12} />}
                    onClick={() => moveSlide(i, i - 1)}
                  />
                )}
                {i < slides?.length - 1 && (
                  <KoButton
                    className={styles.smallBtn}
                    icon={
                      <KoIcon name="ArrowDownOutlined" width={12} height={12} />
                    }
                    onClick={() => moveSlide(i, i + 1)}
                  />
                )}
                <KoButton
                  className={styles.smallBtn}
                  icon={<KoIcon name="CopyOutlined" width={12} height={12} />}
                  onClick={() => duplicateSlide(i)}
                />
                <KoButton
                  className={styles.smallBtn}
                  icon={<KoIcon name="DeleteOutlined" width={12} height={12} />}
                  onClick={() => deleteSlide(i)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
